import api from '@/lib/axios';
import type { PaginatedResult } from '@/components/data-table';

// ───── Types ────────────────────────────────────────────────────────────────
// Mirrors the raw-SQL-provisioned, legacy-migrated academic_years/classes/
// sections/mediums tables (see backend docs/DEVIATIONS.md) — a different
// shape from the older `AcademicYear`/`ClassEntity`/`Section` interfaces in
// `school.api.ts` (which model school_id + academicYearId-scoped classes),
// so these are kept separate rather than reusing/extending those.

export interface AcademicYear {
  id: string;
  legacyId: number;
  name: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ClassSection {
  id: string;
  name: string;
}

export interface AcademicClass {
  id: string;
  legacyId: number;
  name: string;
  mediumId: string;
  mediumName: string;
  includeSemesters: boolean;
  sectionCount: number;
  sections: ClassSection[];
}

export interface Section {
  id: string;
  /** Null for organic (non-migrated) sections. */
  legacyId: number | null;
  classId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  /** Only present when fetched via SectionsApi.list() (Class Section & Teachers list view). */
  className?: string;
  classTeacherId?: string | null;
  classTeacherName?: string | null;
  /** Only present when fetched via SectionsApi.list() — one entry per (subject, teacher) pair currently assigned in this section. */
  subjectTeachers?: { subjectName: string; teacherName: string | null }[];
}

export interface SectionSubjectTeacherAssignment {
  subjectTeacherId: string;
  teacherId: string;
  teacherName: string | null;
}

export interface SectionSubjectRow {
  subjectId: string;
  subjectName: string;
  /** Co-teaching allowed — 0, 1, or more teachers per subject in this section. */
  teachers: SectionSubjectTeacherAssignment[];
}

export interface SectionTeachers {
  classTeacherId: string | null;
  classTeacherName: string | null;
  subjects: SectionSubjectRow[];
}

export interface Medium {
  id: string;
  legacyId: number;
  name: string;
}

export interface SubjectListItem {
  id: string;
  legacyId: number;
  name: string;
  type: string;
  classCount: number;
}

export interface SubjectAssignment {
  classId: string;
  className: string;
  sectionId: string | null;
  sectionName: string | null;
  teacherId: string | null;
  /**
   * Rendered as-is, including known-doubled names like "Muneera T K Muneera
   * T K" — a legacy source-data issue (see docs/data-issues.md), not
   * something to clean up or hide in the UI.
   */
  teacherName: string | null;
}

export interface SubjectDetail {
  id: string;
  legacyId: number;
  name: string;
  type: string;
  assignments: SubjectAssignment[];
}

export interface Department {
  id: string;
  legacyId: number | null;
  name: string;
  description: string | null;
  teacherCount: number;
}

export interface ClassSubjectRow {
  /** The real class_subjects.id — needed for AcademicClassesApi.removeSubject(). Repeats across rows for a class with multiple sections (one row per subject+section+teacher combination); dedupe by subjectId for a per-class subject list. */
  classSubjectId: string;
  /** Compulsory/Elective (class_subjects.type) — NOT the same as subjectType below (subjects' own Theory/Practical-style type). Easy to conflate by name; keep them distinct. */
  classSubjectType: string;
  subjectId: string;
  subjectName: string;
  subjectType: string;
  sectionId: string | null;
  sectionName: string | null;
  teacherId: string | null;
  teacherName: string | null;
}

export const CLASS_SUBJECT_TYPES = ['Compulsory', 'Elective'] as const;
export type ClassSubjectType = (typeof CLASS_SUBJECT_TYPES)[number];

export interface AcademicYearListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: 'name' | 'start_date' | 'is_current';
  sortOrder?: 'asc' | 'desc';
  isCurrent?: boolean;
}

export interface ClassListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: 'name' | 'medium';
  sortOrder?: 'asc' | 'desc';
}

export interface SubjectListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: 'name' | 'type' | 'classCount';
  sortOrder?: 'asc' | 'desc';
}

export const NO_CURRENT_ACADEMIC_YEAR = 'NO_CURRENT_ACADEMIC_YEAR';

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export const AcademicYearsApi = {
  list: async (
    params?: AcademicYearListParams,
  ): Promise<PaginatedResult<AcademicYear>> =>
    unwrap(await api.get('/academic-years', { params })),

  current: async (): Promise<AcademicYear> =>
    unwrap(await api.get('/academic-years/current')),
};

export interface CreateClassData {
  name: string;
  mediumId: string;
  /** Creates these as brand-new section rows scoped to the new class — see docs/DEVIATIONS.md (no "pick existing name" concept in this schema). */
  sectionNames: string[];
}

export interface UpdateClassData {
  name?: string;
  mediumId?: string;
}

export const AcademicClassesApi = {
  list: async (
    params?: ClassListParams,
  ): Promise<PaginatedResult<AcademicClass>> =>
    unwrap(await api.get('/classes', { params })),

  get: async (id: string): Promise<AcademicClass> =>
    unwrap(await api.get(`/classes/${id}`)),

  /** Subjects taught to this class, with the teacher for each section. */
  subjects: async (id: string): Promise<ClassSubjectRow[]> =>
    unwrap(await api.get(`/classes/${id}/subjects`)),

  create: async (data: CreateClassData): Promise<AcademicClass> =>
    unwrap(await api.post('/classes', data)),

  update: async (id: string, data: UpdateClassData): Promise<AcademicClass> =>
    unwrap(await api.patch(`/classes/${id}`, data)),

  remove: async (id: string): Promise<void> => unwrap(await api.delete(`/classes/${id}`)),

  assignSubject: async (
    classId: string,
    data: { subjectId: string; type: ClassSubjectType },
  ): Promise<{ id: string; classId: string; subjectId: string; subjectName: string; type: ClassSubjectType }> =>
    unwrap(await api.post(`/classes/${classId}/subjects`, data)),

  removeSubject: async (classId: string, classSubjectId: string): Promise<void> =>
    unwrap(await api.delete(`/classes/${classId}/subjects/${classSubjectId}`)),

  listElectiveGroups: async (classId: string): Promise<ElectiveGroup[]> =>
    unwrap(await api.get(`/classes/${classId}/elective-groups`)),

  createElectiveGroup: async (classId: string, data: { subjectIds: string[]; totalSelectableSubjects: number }): Promise<ElectiveGroup> =>
    unwrap(await api.post(`/classes/${classId}/elective-groups`, data)),

  updateElectiveGroup: async (classId: string, groupId: string, totalSelectableSubjects: number): Promise<void> =>
    unwrap(await api.patch(`/classes/${classId}/elective-groups/${groupId}`, { totalSelectableSubjects })),

  removeElectiveGroup: async (classId: string, groupId: string): Promise<void> =>
    unwrap(await api.delete(`/classes/${classId}/elective-groups/${groupId}`)),

  addElectiveGroupSubject: async (
    classId: string,
    groupId: string,
    subjectId: string,
  ): Promise<{ classSubjectId: string; subjectId: string; subjectName: string }> =>
    unwrap(await api.post(`/classes/${classId}/elective-groups/${groupId}/subjects`, { subjectId })),
};

export interface ElectiveGroupSubject {
  classSubjectId: string;
  subjectId: string;
  subjectName: string;
}

export interface ElectiveGroup {
  id: string;
  classId: string;
  totalSelectableSubjects: number;
  subjects: ElectiveGroupSubject[];
}

export interface CreateSectionData {
  name: string;
  classId: string;
}

export const SectionsApi = {
  list: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    classId?: string;
  }): Promise<PaginatedResult<Section>> =>
    unwrap(await api.get('/sections', { params })),

  create: async (data: CreateSectionData): Promise<Section> =>
    unwrap(await api.post('/sections', data)),

  update: async (id: string, name: string): Promise<Section> =>
    unwrap(await api.patch(`/sections/${id}`, { name })),

  remove: async (id: string): Promise<void> => unwrap(await api.delete(`/sections/${id}`)),

  getTeachers: async (sectionId: string): Promise<SectionTeachers> =>
    unwrap(await api.get(`/sections/${sectionId}/teachers`)),

  /** Reassigns the teacher here if they were already class-teacher of another section in this school — `previousSections` lists what got cleared. */
  setClassTeacher: async (sectionId: string, teacherId: string | null): Promise<{ previousSections: { id: string; name: string }[] }> =>
    unwrap(await api.put(`/sections/${sectionId}/class-teacher`, { teacherId })),

  assignSubjectTeacher: async (sectionId: string, data: { subjectId: string; teacherId: string }): Promise<void> =>
    unwrap(await api.put(`/sections/${sectionId}/subject-teacher`, data)),

  unassignSubjectTeacher: async (sectionId: string, subjectTeacherId: string): Promise<void> =>
    unwrap(await api.delete(`/sections/${sectionId}/subject-teacher/${subjectTeacherId}`)),
};

export const MediumsApi = {
  list: async (): Promise<Medium[]> => unwrap(await api.get('/mediums')),
};

export const SubjectsApi = {
  list: async (
    params?: SubjectListParams,
  ): Promise<PaginatedResult<SubjectListItem>> =>
    unwrap(await api.get('/subjects', { params })),

  get: async (id: string): Promise<SubjectDetail> =>
    unwrap(await api.get(`/subjects/${id}`)),
};

export const DepartmentsApi = {
  list: async (): Promise<Department[]> => unwrap(await api.get('/departments')),

  create: async (data: { name: string; description?: string }): Promise<Department> =>
    unwrap(await api.post('/departments', data)),

  update: async (id: string, data: { name?: string; description?: string }): Promise<Department> =>
    unwrap(await api.patch(`/departments/${id}`, data)),

  remove: async (id: string): Promise<void> => unwrap(await api.delete(`/departments/${id}`)),
};

/** True when `err` is the axios error for a 404 NO_CURRENT_ACADEMIC_YEAR response. */
export function isNoCurrentYearError(err: unknown): boolean {
  return (
    (err as any)?.response?.data?.error?.code === NO_CURRENT_ACADEMIC_YEAR
  );
}

// ───── Academic Calendar (over the migrated `academic_calendar` table —
// 151 migrated events: Exam / Teachers Training / Opening Ceremony, plus
// organically-created ones via the create form below. Working Day rows
// were never migrated in bulk, but real historical ones have since been
// backfilled per-school from attendance evidence, and the type is now
// creatable one-off too — see docs/DEVIATIONS.md) ──────────────────────

export interface AcademicCalendarDefaultYear {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
}

/**
 * The real old-system create-form type list (from a direct screenshot
 * reference), not derived from the migrated data — `type` is free text in
 * the legacy schema, so the true vocabulary can't be read from the
 * database. Only 3 of these 10 ever had a MIGRATED row in the legacy data
 * (Exam/Teachers Training/Opening Ceremony); 'Working Day' rows now exist
 * too, backfilled from real attendance evidence rather than migrated in
 * bulk (see docs/DEVIATIONS.md) — re-enabled here as creatable, a
 * conscious reversal of the earlier exclusion, mirrored from the backend
 * DTO. The remaining 6 are old-system options that were apparently never
 * actually used. Hardcoded rather than a dedicated types endpoint, kept
 * minimal.
 */
export const ACADEMIC_CALENDAR_TYPES = [
  'Exam',
  'Teachers Training',
  'Opening Ceremony',
  'Parent Evaluation',
  'Home Schooling',
  'Field Trip',
  'Al Ithqaan Arts Fest',
  'Quran Revision Day',
  'Celebrations Day',
  'Working Day',
] as const;
export type AcademicCalendarType = (typeof ACADEMIC_CALENDAR_TYPES)[number];

export const ACADEMIC_CALENDAR_TERMS = ['Term 1', 'Term 2', 'Term 3'] as const;
export type AcademicCalendarTerm = (typeof ACADEMIC_CALENDAR_TERMS)[number];

export interface AcademicCalendarEvent {
  id: string;
  /** Null for organic (non-migrated) rows created via the form below. */
  legacyId: number | null;
  name: string;
  description: string | null;
  type: string;
  color: string;
  term: string | null;
  date: string;
  academicYearId: string | null;
  academicYearName: string | null;
  addedByName: string | null;
}

export interface AcademicCalendarListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortOrder?: 'asc' | 'desc';
  academicYearId?: string;
  term?: string;
  month?: number;
  type?: string;
}

export interface AcademicCalendarWriteData {
  name: string;
  description?: string;
  type: AcademicCalendarType;
  /** Plain 'YYYY-MM-DD' — never a Date object, never reformatted client-side. See AcademicCalendarPage.tsx. */
  date: string;
  term?: AcademicCalendarTerm;
  academicYearId?: string;
}

export interface GenerateAcademicCalendarData {
  months: number[];
  term: AcademicCalendarTerm;
  academicYearId: string;
}

export interface GenerateAcademicCalendarResult {
  created: number;
  skipped: number;
  dates: string[];
}

export const AcademicCalendarApi = {
  defaultYear: async (): Promise<AcademicCalendarDefaultYear | null> =>
    unwrap(await api.get('/academic-calendar/default-year')),

  list: async (
    params?: AcademicCalendarListParams,
  ): Promise<PaginatedResult<AcademicCalendarEvent>> =>
    unwrap(await api.get('/academic-calendar', { params })),

  month: async (year: number, month: number): Promise<AcademicCalendarEvent[]> =>
    unwrap(await api.get(`/academic-calendar/month/${year}/${month}`)),

  create: async (data: AcademicCalendarWriteData): Promise<AcademicCalendarEvent> =>
    unwrap(await api.post('/academic-calendar', data)),

  generate: async (data: GenerateAcademicCalendarData): Promise<GenerateAcademicCalendarResult> =>
    unwrap(await api.post('/academic-calendar/generate', data)),

  update: async (id: string, data: Partial<AcademicCalendarWriteData>): Promise<AcademicCalendarEvent> =>
    unwrap(await api.patch(`/academic-calendar/${id}`, data)),

  remove: async (id: string): Promise<void> => unwrap(await api.delete(`/academic-calendar/${id}`)),
};

// ───── Lessons & Topics — school-tier only, brand-new feature (no legacy
// precedent, see docs/DEVIATIONS.md). YouTube links + document uploads
// only, no native video hosting; documents go through the existing
// UploadApi.upload() (below) first, then the returned url is attached here. ─

export const LESSON_FILE_TYPES = ['document', 'youtube', 'link'] as const;
export type LessonFileType = (typeof LESSON_FILE_TYPES)[number];

export interface LessonFile {
  id: string;
  fileType: LessonFileType;
  fileUrl: string | null;
  youtubeUrl: string | null;
  title: string | null;
  thumbnailUrl: string | null;
  /** Set only for migrated rows whose real file/link couldn't be located — see docs/data-issues.md. */
  note: string | null;
  createdAt: string;
}

export interface LessonListRow {
  id: string;
  sectionId: string;
  sectionName: string;
  classId: string;
  className: string;
  classSubjectId: string;
  subjectName: string;
  name: string;
  description: string | null;
  createdByName: string | null;
  fileCount: number;
  topicCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface LessonDetail extends LessonListRow {
  files: LessonFile[];
}

export interface LessonTopicListRow {
  id: string;
  lessonId: string;
  sectionId: string;
  sectionName: string;
  className: string;
  classSubjectId: string;
  subjectName: string;
  name: string;
  description: string | null;
  fileCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface LessonTopicDetail extends LessonTopicListRow {
  files: LessonFile[];
}

export interface CreateLessonData {
  sectionId: string;
  classSubjectId: string;
  name: string;
  description?: string;
}

export interface UpdateLessonData {
  sectionId?: string;
  classSubjectId?: string;
  name?: string;
  description?: string;
}

export interface CreateLessonFileData {
  fileType: LessonFileType;
  fileUrl?: string;
  youtubeUrl?: string;
  title?: string;
}

export interface LessonListParams {
  page?: number;
  limit?: number;
  sectionId?: string;
  classSubjectId?: string;
  classId?: string;
}

export const LessonsApi = {
  list: async (params?: LessonListParams): Promise<PaginatedResult<LessonListRow>> =>
    unwrap(await api.get('/school/lessons', { params })),

  get: async (id: string): Promise<LessonDetail> => unwrap(await api.get(`/school/lessons/${id}`)),

  create: async (data: CreateLessonData): Promise<LessonListRow> => unwrap(await api.post('/school/lessons', data)),

  update: async (id: string, data: UpdateLessonData): Promise<LessonListRow> =>
    unwrap(await api.patch(`/school/lessons/${id}`, data)),

  remove: async (id: string): Promise<void> => unwrap(await api.delete(`/school/lessons/${id}`)),

  addFile: async (id: string, data: CreateLessonFileData): Promise<LessonFile> =>
    unwrap(await api.post(`/school/lessons/${id}/files`, data)),

  removeFile: async (id: string, fileId: string): Promise<void> =>
    unwrap(await api.delete(`/school/lessons/${id}/files/${fileId}`)),
};

export interface CreateLessonTopicData {
  lessonId: string;
  sectionId: string;
  classSubjectId: string;
  name: string;
  description?: string;
}

export interface UpdateLessonTopicData {
  sectionId?: string;
  classSubjectId?: string;
  name?: string;
  description?: string;
}

export const LessonTopicsApi = {
  list: async (params: { lessonId: string; page?: number; limit?: number }): Promise<PaginatedResult<LessonTopicListRow>> =>
    unwrap(await api.get('/school/lesson-topics', { params })),

  get: async (id: string): Promise<LessonTopicDetail> => unwrap(await api.get(`/school/lesson-topics/${id}`)),

  create: async (data: CreateLessonTopicData): Promise<LessonTopicListRow> =>
    unwrap(await api.post('/school/lesson-topics', data)),

  update: async (id: string, data: UpdateLessonTopicData): Promise<LessonTopicListRow> =>
    unwrap(await api.patch(`/school/lesson-topics/${id}`, data)),

  remove: async (id: string): Promise<void> => unwrap(await api.delete(`/school/lesson-topics/${id}`)),

  addFile: async (id: string, data: CreateLessonFileData): Promise<LessonFile> =>
    unwrap(await api.post(`/school/lesson-topics/${id}/files`, data)),

  removeFile: async (id: string, fileId: string): Promise<void> =>
    unwrap(await api.delete(`/school/lesson-topics/${id}/files/${fileId}`)),
};
